import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { BlogCard } from "@/components/blog/BlogCard";
import { CategoryChips } from "@/components/blog/CategoryChips";
import { getBlogList } from "@/lib/api";
import { SITE_ORIGIN } from "@/lib/site";

const DEFAULT_COPY = {
  eyebrow: "Blog & Rehberler",
  title: "Düğün fotoğrafçılığı rehberi",
  description:
    "Fotoğrafçı seçiminden çekim günü planlamasına, Ankara'nın en iyi mekânlarından ilçe ilçe rehberlere: düğün fotoğrafınız için bilmeniz gereken her şey.",
};

export default function BlogList() {
  const [params] = useSearchParams();
  const category = params.get("kategori") || "all";

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["blog", "list", category],
    queryFn: () => getBlogList({ limit: 100, category: category === "all" ? undefined : category }),
    retry: 1,
  });

  const posts = (data && data.items) || [];
  const categories = (data && data.categories) || [];
  const allTotal = categories.reduce((s, c) => s + (c.count || 0), 0);
  const activeCat = categories.find((c) => c.slug === category);
  const featured = posts[0];
  const rest = posts.slice(1);

  const copy = activeCat
    ? { eyebrow: "Kategori", title: activeCat.name, description: activeCat.description }
    : DEFAULT_COPY;

  const seoTitle = activeCat
    ? `${activeCat.name} | Ankara Düğün Fotoğrafçılığı Blog`
    : "Blog | Düğün Fotoğrafçılığı Rehberi ve Ankara İlçe Rehberleri";
  const seoPath = activeCat ? `/blog?kategori=${activeCat.slug}` : "/blog";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seoTitle,
    description: copy.description,
    url: `${SITE_ORIGIN}${seoPath}`,
    inLanguage: "tr-TR",
  };

  return (
    <>
      <Seo title={seoTitle} description={copy.description} path={seoPath} jsonLd={jsonLd} />

      <section className="pt-28 sm:pt-32 lg:pt-40 pb-8 bg-[#0E0F10]">
        <div className="container-lux text-center max-w-2xl mx-auto">
          <span className="eyebrow justify-center">{copy.eyebrow}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F7F2E8] mt-3" data-testid="blog-list-title">
            {copy.title}
          </h1>
          <p className="text-[#9A9387] mt-4" data-testid="blog-list-description">{copy.description}</p>
        </div>
        <div className="container-lux mt-8">
          {isLoading && !categories.length ? (
            <div className="flex flex-wrap gap-2 justify-center">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-28 rounded-full bg-white/5" />
              ))}
            </div>
          ) : (
            <CategoryChips categories={categories} active={category} total={allTotal} />
          )}
        </div>
      </section>

      <section className="pb-24 bg-[#0E0F10]" data-testid="blog-list">
        <div className="container-lux">
          {isLoading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl overflow-hidden border border-white/10 bg-[#141517]">
                  <Skeleton className="aspect-[16/10] w-full rounded-none bg-white/5" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-3 w-24 bg-white/5" />
                    <Skeleton className="h-6 w-full bg-white/5" />
                    <Skeleton className="h-4 w-3/4 bg-white/5" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isError && (
            <div className="text-center py-16" data-testid="blog-list-error">
              <p className="text-[#9A9387]">Yazılar yüklenirken bir sorun oluştu.</p>
              <Button onClick={() => refetch()} className="mt-4 bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A]">
                Tekrar dene
              </Button>
            </div>
          )}

          {!isLoading && !isError && posts.length === 0 && (
            <div className="text-center py-16" data-testid="blog-list-empty">
              <FileText className="w-10 h-10 text-[#C8A24A] mx-auto" />
              <p className="text-[#EDE7DA] mt-4 font-display text-2xl">Bu kategoride henüz yazı yok</p>
              <p className="text-[#9A9387] mt-2 text-sm">Yeni rehberler çok yakında burada olacak.</p>
              <Link to="/blog" className="inline-block mt-5">
                <Button variant="outline" className="border-white/20 text-[#F7F2E8] bg-transparent hover:bg-white/5">
                  Tüm yazılara dön
                </Button>
              </Link>
            </div>
          )}

          {featured && (
            <Reveal>
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="blog-featured-card"
                className="group grid lg:grid-cols-2 gap-6 rounded-3xl overflow-hidden border border-white/10 bg-[#141517] mb-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8A24A]"
              >
                <div className="aspect-[16/11] lg:aspect-auto overflow-hidden">
                  <img
                    src={featured.hero_image}
                    alt={featured.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-7 lg:p-10 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#C8A24A]">
                    <span>{featured.category_name || "Rehber"}</span>
                    {featured.district_name && (
                      <>
                        <span aria-hidden>·</span>
                        <span>{featured.district_name}</span>
                      </>
                    )}
                    <span aria-hidden>·</span>
                    <span>Öne Çıkan</span>
                  </div>
                  <h2 className="font-display text-3xl lg:text-4xl text-[#F7F2E8] mt-3 leading-tight">{featured.title}</h2>
                  <p className="text-[#9A9387] mt-3 line-clamp-3">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 mt-5">
                    <span className="inline-flex items-center gap-1 text-[#D7B86A] group-hover:gap-2 transition-[gap] duration-200">
                      Devamını oku <ArrowRight className="w-4 h-4" />
                    </span>
                    {featured.reading_minutes ? (
                      <span className="inline-flex items-center gap-1 text-sm text-[#7C766B]">
                        <Clock className="w-3.5 h-3.5" /> {featured.reading_minutes} dk okuma
                      </span>
                    ) : null}
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 6) * 0.05}>
                <BlogCard post={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
